import React, { ReactNode, useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'
import formatWithOptions from 'date-fns/fp/formatWithOptions'
import deLocale from 'date-fns/locale/de'
import enLocale from 'date-fns/locale/en-US'
import { useField } from 'formik'

import { FieldSettings, FormHelpersProviderReturn, Form } from '../../../ana-toolbox'
import { DATE_FORMATS } from '../../utils/constants/date'
import { getStartOfMonthFiveYearsAgo } from '../../utils/helpers/date'
import { getHasError } from '../../utils/helpers/getHasError'
import { useFormHelpers } from '../../utils/hooks/useFormHelpers'
import { useSubmitCount } from '../../utils/hooks/useSubmitCount'
import { TextField } from '../../utils/shared/TextField'
import { TextFieldLabel } from '../../utils/shared/TextFieldLabel'
import { combinedFields } from '../../utils/yup/main'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

import * as S from './date-picker-field.styles'

export type DatePickerFieldProps = {
  name: string
  value: Date | string
  label: ReactNode
  error?: string
  helperText?: ReactNode
  isRequired?: boolean | ((formValues: Partial<Form>) => boolean)
  onChange: (event: React.ChangeEvent<{ name: string; value: Date | string }>) => void
  onBlur?: (event: React.FocusEvent) => void
}

const today = new Date()
const fiveYearsAgo = getStartOfMonthFiveYearsAgo()

const DatePickerFieldBase = ({ label, value, name, helperText, isRequired, error, onChange }: DatePickerFieldProps) => {
  const { i18n } = useTranslation()

  const handleChange = (date: Date | null) => {
    if (date) {
      onChange?.({ target: { name, value: date } } as React.ChangeEvent<{ name: string; value: Date }>)
    }
  }

  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  const handleClick = (event: React.MouseEvent<Element>) => {
    setAnchorEl(event.target as Element)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined

  const formattedValue = useMemo(() => {
    if (!value) return ''

    return formatWithOptions({
      locale: i18n.language === 'en' ? enLocale : deLocale,
    })(DATE_FORMATS.LOCAL_MONTH_YEAR)(new Date(value))
  }, [value, i18n])

  const handleMonthChange = () => setAnchorEl(null)

  return (
    <S.Container>
      <S.Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose}>
        <S.DatePickerWrapper>
          <StaticDatePicker
            value={typeof value === 'string' ? new Date(value) : value}
            onChange={handleChange}
            onMonthChange={handleMonthChange}
            views={['year', 'month']}
            minDate={fiveYearsAgo}
            maxDate={today}
            slotProps={{ actionBar: { actions: [] } }}
          />
        </S.DatePickerWrapper>
      </S.Popover>

      <TextField
        label={
          <TextFieldLabel required={typeof isRequired === 'function' ? false : isRequired}>
            <Translate>{label}</Translate>
          </TextFieldLabel>
        }
        error={Boolean(error)}
        name={name}
        value={formattedValue}
        InputLabelProps={{
          id: `${name}-label`,
        }}
        inputProps={{ readOnly: true, onClick: handleClick, 'aria-labelledby': `${name}-label` }}
      />

      <FieldHelperText error={error} helperText={helperText} name={name} />
    </S.Container>
  )
}

export const DatePickerField = (props: DatePickerFieldProps) => {
  const submitCount = useSubmitCount()
  const formHelpers = useFormHelpers()
  const [field, meta] = useField(props.name)

  const hasError = useMemo(() => getHasError(meta, submitCount ?? 1), [meta, submitCount])

  const fieldSettings = useMemo(
    () => combinedFields[props.name as keyof typeof combinedFields] as FieldSettings,
    [props.name]
  )

  const handleChange = useCallback(
    (e: React.ChangeEvent<{ name: string; value: string | Date }>) => {
      fieldSettings?.hooks?.beforeChange?.(e.target.value, formHelpers as FormHelpersProviderReturn)
      field.onChange(e)
      props.onChange?.(e)
    },
    // Ignoring these dependencies to prevent unnecessary re renderes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return <DatePickerFieldBase error={hasError ? meta.error : undefined} {...props} {...field} onChange={handleChange} />
}
